/**
 * Recency helpers for the sidebar "Chat history" list (ENG-1161).
 *
 * Pure functions so the grouping boundaries and the compact age labels are
 * unit-testable without rendering the sidebar.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Compact relative age, as the sidebar's right-hand column shows it:
 * `now`, `5m`, `3h`, `89d`, `2y`. Future timestamps (clock skew) read `now`.
 */
export function formatCompactAge(value: Date | string, now: Date): string {
  const elapsed = now.getTime() - new Date(value).getTime();
  if (elapsed < MINUTE) return 'now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h`;
  const days = Math.floor(elapsed / DAY);
  if (days < 365) return `${days}d`;
  return `${Math.floor(days / 365)}y`;
}

export type RecencyGroupKey = 'today' | 'week' | 'month' | 'older';

export const recencyGroupLabels: Record<RecencyGroupKey, string> = {
  today: 'Today',
  week: 'Previous 7 days',
  month: 'Previous 30 days',
  older: 'Older',
};

const groupOrder: RecencyGroupKey[] = ['today', 'week', 'month', 'older'];

export interface RecencyGroup<T> {
  key: RecencyGroupKey;
  label: string;
  items: T[];
}

/**
 * Bucket items by `updatedAt` against the viewer's local calendar day:
 * today, the 7 days before it, the 30 days before it, then everything older.
 * Input order is preserved inside each bucket (the API returns newest first),
 * and empty buckets are dropped.
 */
export function groupByRecency<T extends { updatedAt: Date | string }>(
  items: T[],
  now: Date
): RecencyGroup<T>[] {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const todayStart = startOfToday.getTime();
  const weekStart = todayStart - 7 * DAY;
  const monthStart = todayStart - 30 * DAY;

  const buckets: Record<RecencyGroupKey, T[]> = {
    today: [],
    week: [],
    month: [],
    older: [],
  };
  for (const item of items) {
    const at = new Date(item.updatedAt).getTime();
    if (at >= todayStart) buckets.today.push(item);
    else if (at >= weekStart) buckets.week.push(item);
    else if (at >= monthStart) buckets.month.push(item);
    else buckets.older.push(item);
  }

  return groupOrder
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({
      key,
      label: recencyGroupLabels[key],
      items: buckets[key],
    }));
}
